import DOMPurify from "dompurify";
import { useContext, useEffect, useState } from "react";
import { Button, Form, InputGroup, Table, Spinner } from "react-bootstrap";
import {
  FiBold,
  FiCode,
  FiEdit2,
  FiFilter,
  FiItalic,
  FiList,
  FiSave,
  FiSearch,
  FiTrash2,
  FiX,
  FiBookOpen,
  FiCheckCircle,
  FiEyeOff,
} from "react-icons/fi";
import AdminMasterLayout from "../../../layouts/admin/AdminMasterLayout";

import { Color } from "@tiptap/extension-color";
import TiptapImage from "@tiptap/extension-image";
import Link from "@tiptap/extension-link";
import TextAlign from "@tiptap/extension-text-align";
import { TextStyle } from "@tiptap/extension-text-style";
import Underline from "@tiptap/extension-underline";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import "../../../assets/css/admin/screens/blog/BlogManagement.css";
import AppContext from "../../../contexts/AppContext";

const BlogManagement = () => {
  const { baseUrl, baseUrlForFiles, adminAuthToken } = useContext(AppContext);

  const [isDark] = useState(false);

  const [title, setTitle] = useState("");
  const [blogimage, setBlogImage] = useState(null);
  const [status, setStatus] = useState("published");
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);

  const [keywords, setKeywords] = useState([]);
  const [keywordInput, setKeywordInput] = useState("");

  const [imageForEdit, setImageForEdit] = useState("");
  const [slug, setSlug] = useState("");
  const [content, setContent] = useState("");
  const [blogs, setBlogs] = useState([]);
  const [blogId, setBlogId] = useState();

  const [showModal, setShowModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("All");

  const sanitizeHTML = (html) => DOMPurify.sanitize(html);

  const generateSlug = (text) =>
    text
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9\s-]/g, "")
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-");

  const handleTitleChange = (e) => {
    const val = e.target.value;
    setTitle(val);
    if (!blogId) setSlug(generateSlug(val));
  };

  const handleKeywordKeyDown = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      const val = keywordInput.trim().replace(",", "");
      if (val && !keywords.includes(val)) setKeywords([...keywords, val]);
      setKeywordInput("");
    }
  };

  const removeKeyword = (index) =>
    setKeywords(keywords.filter((_, i) => i !== index));

  const resetForm = () => {
    setBlogId(null);
    setTitle("");
    setKeywords([]);
    setKeywordInput("");
    setSlug("");
    setContent("");
    setBlogImage(null);
    setImageForEdit("");
    if (editor) editor.commands.clearContent();
  };

  const createBlog = () => {
    const loggedInAdmin = JSON.parse(localStorage.getItem("loggedInAdmin"));
    const adminId = loggedInAdmin?.id;
    const formdata = new FormData();
    formdata.append("chadmin_id", adminId);
    formdata.append("title", title);
    formdata.append("blogimage", blogimage);
    formdata.append("keyword", keywords.join(", "));
    formdata.append("slug", slug);
    formdata.append("content", DOMPurify.sanitize(content));
    formdata.append("status", status);
    axios
      .post(`${baseUrl}/admin/blog/createblog`, formdata, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${adminAuthToken}`,
        },
      })
      .then((response) => {
        toast.success("Blog Created");
        console.log(response);
        viewAllBlog();
        viewBlogStats();
        setShowModal(false);
      })
      .catch((error) => console.log(error));
  };

  const viewAllBlog = () => {
    const loggedInAdmin = JSON.parse(localStorage.getItem("loggedInAdmin"));
    const adminId = loggedInAdmin?.id;
    setLoading(true);
    axios
      .post(
        `${baseUrl}/admin/blog/viewAllBlog`,
        { chadmin_id: adminId },
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${adminAuthToken}`,
          },
        },
      )
      .then((response) => {
        console.log(response.data);
        setBlogs(response.data.blog);
        setLoading(false);
      })
      .catch((error) => {
        console.log(error);
        setLoading(false);
      });
  };

  const viewBlogStats = () => {
    axios
      .get(`${baseUrl}/admin/blog/stats`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${adminAuthToken}`,
        },
      })
      .then((response) => setStats(response.data))
      .catch((error) => console.log(error));
  };

  const updateBlog = () => {
    const loggedInAdmin = JSON.parse(localStorage.getItem("loggedInAdmin"));
    const adminId = loggedInAdmin?.id;
    const formdata = new FormData();
    formdata.append("chadmin_id", adminId);
    formdata.append("blogid", blogId);
    formdata.append("title", title);
    formdata.append("blogimage", blogimage);
    formdata.append("keyword", keywords.join(", "));
    formdata.append("slug", slug);
    formdata.append("content", DOMPurify.sanitize(content));
    formdata.append("status", status);
    axios
      .post(`${baseUrl}/admin/blog/updateBlog`, formdata, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${adminAuthToken}`,
        },
      })
      .then((response) => {
        toast.info("Blog Updated");
        console.log(response.data);
        viewAllBlog();
        viewBlogStats();
        setShowModal(false);
        resetForm();
      })
      .catch((error) => console.log(error));
  };

  const deleteBlog = (id) => {
    const formdata = new FormData();
    formdata.append("blogid", id);
    axios
      .post(`${baseUrl}/admin/blog/deleteBlog`, formdata, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${adminAuthToken}`,
        },
      })
      .then((response) => {
        toast.error("Blog Deleted");
        console.log(response.data);
        viewAllBlog();
        viewBlogStats();
      })
      .catch((error) => console.log(error));
  };

  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      TextStyle,
      Color,
      Link.configure({ openOnClick: false }),
      TiptapImage,
      TextAlign.configure({ types: ["heading", "paragraph"] }),
    ],
    content: content,
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      setContent(html);
    },
  });

  useEffect(() => {
    viewAllBlog();
    viewBlogStats();
  }, []);

  useEffect(() => {
    if (editor && content) editor.commands.setContent(content);
  }, [content, editor]);

  const filteredBlogs = blogs.filter((blog) => {
    const matchesSearch =
      blog.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      blog.keyword?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      String(blog.id).includes(searchTerm);
    const matchesStatus =
      filterStatus === "All" ||
      blog.status?.toLowerCase() === filterStatus.toLowerCase();
    return matchesSearch && matchesStatus;
  });

  const getStatusClass = (s) => {
    if (!s) return "";
    if (s.toLowerCase() === "published") return "blog-status--published";
    if (s.toLowerCase() === "draft") return "blog-status--draft";
    return "";
  };

  return (
    <AdminMasterLayout>
      <ToastContainer position="top-right" autoClose={3000} />
      <div className={`blog-card-shell ${isDark ? "dark-mode" : ""}`}>
        {/* Header */}
        <div className="blog-header-section">
          <div>
            <h3 className="blog-section-title">Blog Management</h3>
            <p className="blog-section-subtitle">
              Manage your blog posts and content
            </p>
          </div>
          <Button
            className="blog-create-btn"
            onClick={() => {
              resetForm();
              setShowModal(true);
            }}
          >
            <span>+</span> Create New Blog
          </Button>
        </div>

        {/* Stat Cards */}
        <div className="blog-summary-grid">
          <div className="blog-summary-card">
            <div>
              <span className="blog-label">Total Blogs</span>
              <h3>{stats?.total_blogs ?? "0"}</h3>
              <p className="blog-muted mb-0">All blog posts</p>
            </div>
            <div className="blog-card-icon">
              <FiBookOpen size={22} />
            </div>
          </div>
          <div className="blog-summary-card">
            <div>
              <span className="blog-label">Published</span>
              <h3>{stats?.published_blogs ?? "0"}</h3>
              <p className="blog-muted mb-0">Live on website</p>
            </div>
            <div className="blog-card-icon blog-card-icon--green">
              <FiCheckCircle size={22} />
            </div>
          </div>
          <div className="blog-summary-card">
            <div>
              <span className="blog-label">Unpublished</span>
              <h3>{stats?.draft_blogs ?? "0"}</h3>
              <p className="blog-muted mb-0">Unpublished posts</p>
            </div>
            <div className="blog-card-icon">
              <FiEyeOff size={22} />
            </div>
          </div>
        </div>

        {/* Toolbar */}
        <div className="blog-toolbar">
          <InputGroup className="blog-search-group">
            <InputGroup.Text className="blog-search-icon">
              <FiSearch size={14} />
            </InputGroup.Text>
            <Form.Control
              className="blog-search-input"
              placeholder="Search by title, keyword or ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </InputGroup>
          <div className="blog-filter-group">
            <FiFilter size={14} />
            <Form.Select
              className="blog-filter-select"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <option value="All">All Status</option>
              <option value="published">Published</option>
              <option value="draft">Draft</option>
            </Form.Select>
          </div>
        </div>

        {/* Table */}
        <div className="blog-layout">
          <div className="blog-table-wrapper">
            <Table className="blog-table" responsive>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Title</th>
                  <th>Image</th>
                  <th>Keywords</th>
                  <th>Slug</th>
                  <th>Content</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td colSpan="8" className="text-center py-4">
                      <Spinner
                        animation="border"
                        size="sm"
                        className="blog-spinner"
                      />
                      <span className="ms-2 blog-muted blog-loading-text">
                        Loading blogs...
                      </span>
                    </td>
                  </tr>
                )}

                {!loading &&
                  filteredBlogs.length > 0 &&
                  filteredBlogs.map((blog) => (
                    <tr key={blog.id}>
                      <td>
                        <span className="blog-id">#{blog.id}</span>
                      </td>
                      <td>
                        <p className="blog-title-text">{blog.title}</p>
                      </td>
                      <td>
                        {blog.blogimage ? (
                          <img
                            src={`${baseUrlForFiles}/${blog.blogimage}`}
                            alt={blog.title}
                            className="blog-table-thumb"
                          />
                        ) : (
                          <span className="blog-muted">No Image</span>
                        )}
                      </td>
                      <td>
                        <div className="blog-keyword-tags">
                          {blog.keyword ? (
                            blog.keyword
                              .split(", ")
                              .filter(Boolean)
                              .map((kw, i) => (
                                <span key={i} className="blog-keyword-badge">
                                  {kw}
                                </span>
                              ))
                          ) : (
                            <span className="blog-muted">—</span>
                          )}
                        </div>
                      </td>
                      <td>
                        <span className="blog-slug-text">/{blog.slug}</span>
                      </td>
                      <td>
                        <div
                          className="pm-desc-box"
                          dangerouslySetInnerHTML={{
                            __html: sanitizeHTML(blog.content),
                          }}
                        />
                      </td>
                      <td>
                        <span
                          className={`blog-status ${getStatusClass(blog.status)}`}
                        >
                          {blog.status || "—"}
                        </span>
                      </td>
                      <td>
                        <div className="blog-actions">
                          <button
                            className="blog-action-btn blog-action-btn--edit"
                            title="Edit"
                            onClick={() => {
                              setBlogId(blog.id);
                              setTitle(blog.title);
                              setImageForEdit(blog.blogimage);
                              setKeywords(
                                blog.keyword
                                  ? blog.keyword.split(", ").filter(Boolean)
                                  : [],
                              );
                              setKeywordInput("");
                              setSlug(blog.slug);
                              setContent(blog.content);
                              setStatus(blog.status);
                              setShowModal(true);
                            }}
                          >
                            <FiEdit2 size={13} />
                          </button>
                          <button
                            className="blog-action-btn blog-action-btn--delete"
                            title="Delete"
                            onClick={() => deleteBlog(blog.id)}
                          >
                            <FiTrash2 size={13} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}

                {!loading && filteredBlogs.length === 0 && (
                  <tr>
                    <td colSpan="8" className="text-center py-5">
                      <div className="blog-empty-icon">📝</div>
                      <p className="blog-muted mt-2">
                        {blogs.length === 0
                          ? "No blogs found"
                          : "No blogs match the selected filters"}
                      </p>
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </div>
      </div>

      {/* ================= MODAL ================= */}
      {showModal && (
        <div className="blog-modal-overlay" onClick={() => setShowModal(false)}>
          <div
            className="blog-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="blog-modal-header">
              <h4>{blogId ? "Edit Blog" : "Create Blog"}</h4>
              <button
                className="blog-modal-close"
                onClick={() => {
                  resetForm();
                  setShowModal(false);
                }}
              >
                <FiX size={20} />
              </button>
            </div>

            <Form className="blog-modal-form">
              <div className="blog-form-grid">
                {/* LEFT */}
                <div className="blog-form-left">
                  <Form.Group className="blog-form-group">
                    <Form.Label>Title</Form.Label>
                    <Form.Control
                      type="text"
                      value={title}
                      onChange={handleTitleChange}
                      placeholder="Enter blog title"
                    />
                  </Form.Group>

                  <Form.Group className="blog-form-group">
                    <Form.Label>Featured Image</Form.Label>
                    {imageForEdit ? (
                      <img
                        src={`${baseUrlForFiles}/${imageForEdit}`}
                        style={{
                          height: 80,
                          width: 80,
                          borderRadius: 8,
                          objectFit: "cover",
                          marginBottom: 8,
                        }}
                        alt="preview"
                      />
                    ) : null}
                    <Form.Control
                      type="file"
                      onChange={(e) => setBlogImage(e.target.files[0])}
                    />
                  </Form.Group>

                  <Form.Group className="blog-form-group">
                    <Form.Label>Keywords</Form.Label>
                    <div className="blog-keyword-input-container">
                      {keywords.map((kw, i) => (
                        <span key={i} className="blog-keyword-tag">
                          {kw}
                          <button
                            type="button"
                            onClick={() => removeKeyword(i)}
                          >
                            ×
                          </button>
                        </span>
                      ))}
                      <input
                        type="text"
                        value={keywordInput}
                        onChange={(e) => setKeywordInput(e.target.value)}
                        onKeyDown={handleKeywordKeyDown}
                        placeholder={
                          keywords.length === 0
                            ? "Type keyword and press Enter"
                            : "Add more..."
                        }
                        className="blog-keyword-raw-input"
                      />
                    </div>
                    <small
                      className="blog-muted"
                      style={{ fontSize: "0.72rem" }}
                    >
                      Press Enter or comma to add each keyword
                    </small>
                  </Form.Group>

                  <Form.Group className="blog-form-group">
                    <div className="blog-slug-label-row">
                      <Form.Label>Slug</Form.Label>
                      {!blogId && (
                        <span className="blog-slug-auto-badge">
                          Auto generated
                        </span>
                      )}
                    </div>
                    <div className="blog-slug-input-wrapper">
                      <span className="blog-slug-prefix">/</span>
                      <Form.Control
                        type="text"
                        value={slug}
                        onChange={(e) => setSlug(generateSlug(e.target.value))}
                        placeholder="blog-url-slug"
                        className="blog-slug-input"
                      />
                    </div>
                    <small
                      className="blog-muted"
                      style={{ fontSize: "0.72rem" }}
                    >
                      Auto-generated from title. You can edit manually too.
                    </small>
                  </Form.Group>

                  <Form.Group className="blog-form-group">
                    <Form.Label>Status</Form.Label>
                    <Form.Select
                      value={status}
                      onChange={(e) => setStatus(e.target.value)}
                      className="blog-status-select"
                    >
                      <option value="published">Published</option>
                      <option value="Draft">Draft</option>
                    </Form.Select>
                  </Form.Group>
                </div>

                {/* RIGHT */}
                <div className="blog-form-right">
                  <Form.Label>Blog Content</Form.Label>
                  {editor && (
                    <div className="blog-tiptap-toolbar">
                      <button
                        type="button"
                        onClick={() =>
                          editor.chain().focus().toggleBold().run()
                        }
                        className={editor.isActive("bold") ? "is-active" : ""}
                      >
                        <FiBold />
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          editor.chain().focus().toggleItalic().run()
                        }
                        className={editor.isActive("italic") ? "is-active" : ""}
                      >
                        <FiItalic />
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          editor.chain().focus().toggleUnderline().run()
                        }
                        className={
                          editor.isActive("underline") ? "is-active" : ""
                        }
                      >
                        U
                      </button>
                      <div className="blog-toolbar-divider"></div>
                      <button
                        type="button"
                        onClick={() =>
                          editor
                            .chain()
                            .focus()
                            .toggleHeading({ level: 1 })
                            .run()
                        }
                        className={
                          editor.isActive("heading", { level: 1 })
                            ? "is-active"
                            : ""
                        }
                      >
                        H1
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          editor
                            .chain()
                            .focus()
                            .toggleHeading({ level: 2 })
                            .run()
                        }
                        className={
                          editor.isActive("heading", { level: 2 })
                            ? "is-active"
                            : ""
                        }
                      >
                        H2
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          editor
                            .chain()
                            .focus()
                            .toggleHeading({ level: 3 })
                            .run()
                        }
                        className={
                          editor.isActive("heading", { level: 3 })
                            ? "is-active"
                            : ""
                        }
                      >
                        H3
                      </button>
                      <div className="blog-toolbar-divider"></div>
                      <button
                        type="button"
                        onClick={() =>
                          editor.chain().focus().toggleBulletList().run()
                        }
                        className={
                          editor.isActive("bulletList") ? "is-active" : ""
                        }
                      >
                        <FiList />
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          editor.chain().focus().toggleOrderedList().run()
                        }
                        className={
                          editor.isActive("orderedList") ? "is-active" : ""
                        }
                      >
                        1.
                      </button>
                      <div className="blog-toolbar-divider"></div>
                      <button
                        type="button"
                        onClick={() =>
                          editor.chain().focus().toggleBlockquote().run()
                        }
                        className={
                          editor.isActive("blockquote") ? "is-active" : ""
                        }
                      >
                        "
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          editor.chain().focus().toggleCodeBlock().run()
                        }
                        className={
                          editor.isActive("codeBlock") ? "is-active" : ""
                        }
                      >
                        <FiCode />
                      </button>
                      <div className="blog-toolbar-divider"></div>
                      <button
                        type="button"
                        onClick={() =>
                          editor.chain().focus().setTextAlign("left").run()
                        }
                        className={
                          editor.isActive({ textAlign: "left" })
                            ? "is-active"
                            : ""
                        }
                      >
                        ⬅
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          editor.chain().focus().setTextAlign("center").run()
                        }
                        className={
                          editor.isActive({ textAlign: "center" })
                            ? "is-active"
                            : ""
                        }
                      >
                        ⬌
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          editor.chain().focus().setTextAlign("right").run()
                        }
                        className={
                          editor.isActive({ textAlign: "right" })
                            ? "is-active"
                            : ""
                        }
                      >
                        ➡
                      </button>
                    </div>
                  )}
                  <EditorContent
                    editor={editor}
                    className="blog-tiptap-editor"
                  />
                </div>
              </div>

              {/* Footer */}
              <div className="blog-modal-footer">
                <button
                  type="button"
                  className="blog-btn-cancel"
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="blog-btn-save"
                  onClick={() => {
                    blogId == null ? createBlog() : updateBlog();
                  }}
                >
                  <FiSave size={14} /> {blogId ? "Update Blog" : "Create Blog"}
                </button>
              </div>
            </Form>
          </div>
        </div>
      )}
    </AdminMasterLayout>
  );
};

export default BlogManagement;
